import { resolveStateCode } from './stateTax';
import { DemoProfile, DemoView, EntityType } from './types';
import { normalizeJourney } from './utils';

const DEMO_TOKEN_STORAGE_KEY = 'demo_token';

const DEMO_IDENTITY: Omit<DemoProfile, 'token' | 'first_name' | 'state' | 'entity_type' | 'view' | 'journey'> = {
    last_name: 'Rivera',
    email: 'owner@acmeconsulting.com',
    organization: 'Acme Consulting',
    profession: 'Consultant',
};

// Same-origin by default — Vite's dev middleware mocks this path locally, and in prod the
// demo site is expected to be hosted behind a proxy that forwards /api/public/* to Django.
// Override with VITE_DEMO_PROFILE_URL for staging-style direct-to-Django setups.
const PROFILE_URL_BASE = (import.meta.env.VITE_DEMO_PROFILE_URL as string | undefined) || '/api/public/demo-profile/';

export function readTokenFromUrlOrStorage(): string | null {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('demo_token');
    if (fromUrl) {
        try {
            localStorage.setItem(DEMO_TOKEN_STORAGE_KEY, fromUrl);
        } catch {
            // ignore quota / private-mode failures — token still works for this session via URL
        }
        params.delete('demo_token');
        const cleanQuery = params.toString();
        const cleanUrl = cleanQuery ? `${window.location.pathname}?${cleanQuery}` : window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        return fromUrl;
    }
    try {
        return localStorage.getItem(DEMO_TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

export function clearStoredToken(): void {
    try {
        localStorage.removeItem(DEMO_TOKEN_STORAGE_KEY);
    } catch {
        // ignore quota / private-mode failures
    }
}

// View and journey ride in the URL so the control bar can flip them with a reload.
function readDemoStateFromUrl(entityType: EntityType): Pick<DemoProfile, 'view' | 'journey'> {
    const params = new URLSearchParams(window.location.search);
    // Only 'launchpad' and 'activated' are persisted views; 'onboarding' is a launch action.
    const view: DemoView = params.get('view') === 'launchpad' ? 'launchpad' : 'activated';
    return { view, journey: normalizeJourney(entityType, params.get('journey')) };
}

export async function fetchDemoProfile(token: string): Promise<DemoProfile | null> {
    const url = `${PROFILE_URL_BASE}?token=${encodeURIComponent(token)}`;
    try {
        // same-origin so the Cloudflare Access (CF_Authorization) cookie rides along
        // while the demo host sits behind OneLogin; harmless once Access is removed
        // at launch (no cookie to send). The DemoToken remains the real authZ.
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) {
            return null;
        }
        const data = (await res.json()) as {
            first_name?: string;
            entity_type?: EntityType;
            state?: string | null;
            lead_uuid?: string | null;
            anonymous_id?: string;
        };
        const entityType: EntityType = data.entity_type === 'llc' ? 'llc' : 'scorp';
        return {
            ...DEMO_IDENTITY,
            token,
            first_name: data.first_name || 'there',
            // Normalized here so every consumer gets a code the demo has tax data for.
            state: resolveStateCode(data.state),
            entity_type: entityType,
            lead_uuid: data.lead_uuid ?? null,
            anonymous_id: data.anonymous_id,
            ...readDemoStateFromUrl(entityType),
        };
    } catch {
        return null;
    }
}
